import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import { useToast } from '../context/ToastContext';
import SchedulePromptModal from './SchedulePromptModal';
import { translateError } from '../utils/errorMessages';
import type { GroupSchedulePreview, ScheduleAction, ScheduleSelection } from '../types/schedulePrompt';

/** One plain-group membership change, run through the D6 prompt. */
export interface GroupChangeOp {
  groupId: string;
  action: ScheduleAction;
  memberIds: number[];
}

/** One op per changed group for a single member: removes first, then adds. */
export const groupChangeOps = (
  memberId: number,
  changes: { add: number[]; remove: number[] },
): GroupChangeOp[] => {
  if (!Number.isFinite(memberId)) return [];
  return [
    ...changes.remove.map((g) => ({ groupId: String(g), action: 'remove' as const, memberIds: [memberId] })),
    ...changes.add.map((g) => ({ groupId: String(g), action: 'add' as const, memberIds: [memberId] })),
  ];
};

interface ActivePrompt extends GroupChangeOp {
  preview: GroupSchedulePreview | null;
  error: string | null;
}

interface GroupChangePromptsProps {
  /** Queue to run; null = idle. A new array starts a new run. */
  ops: GroupChangeOp[] | null;
  memberNames: Record<string, string>;
  /** Called once the queue is through; `skipped` = ops the admin cancelled. */
  onDone: (result: { applied: number; skipped: number }) => void;
}

/**
 * Runs group adds/removes one D6 prompt at a time (same flow as the group page).
 * An add with nothing scheduled is applied without asking; cancel skips that op.
 */
const GroupChangePrompts: React.FC<GroupChangePromptsProps> = ({ ops, memberNames, onDone }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const [prompt, setPrompt] = useState<ActivePrompt | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [groupNames, setGroupNames] = useState<Record<string, string>>({});
  const queueRef = useRef<GroupChangeOp[]>([]);
  const tallyRef = useRef({ applied: 0, skipped: 0 });
  // Bumped per op, so a late preview of a cancelled op is ignored.
  const seqRef = useRef(0);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const commit = async (op: GroupChangeOp, selection: ScheduleSelection | null) => {
    // No selection = only the group membership changes.
    const payload = {
      memberIds: op.memberIds,
      ...(selection ? { templateIds: selection.templateIds, eventIds: selection.eventIds } : {}),
    };
    if (op.action === 'add') await api.groups.addMembers(op.groupId, payload);
    else await api.groups.removeMembers(op.groupId, payload);
    tallyRef.current.applied += 1;
  };

  const next = async (): Promise<void> => {
    const op = queueRef.current.shift();
    if (!op) {
      setPrompt(null);
      onDoneRef.current({ ...tallyRef.current });
      return;
    }
    const seq = ++seqRef.current;
    setPrompt({ ...op, preview: null, error: null });
    try {
      const { data } = await api.groups.schedulePreview(op.groupId, op.action, op.memberIds);
      if (seq !== seqRef.current) return;
      const empty = data.templates.length === 0 && data.events.length === 0 && data.charges.length === 0;
      if (op.action === 'add' && empty) {
        await commit(op, null);
        return next();
      }
      setPrompt((p) => (p ? { ...p, preview: data } : p));
    } catch (err: any) {
      if (seq !== seqRef.current) return;
      setPrompt((p) => (p ? { ...p, error: translateError(err, t) } : p));
    }
  };

  // Same array = same run (StrictMode re-runs effects in dev).
  const startedRef = useRef<GroupChangeOp[] | null>(null);
  useEffect(() => {
    if (!ops || ops.length === 0 || startedRef.current === ops) return;
    startedRef.current = ops;
    queueRef.current = [...ops];
    tallyRef.current = { applied: 0, skipped: 0 };
    api.groups
      .list()
      .then((r) => {
        const names: Record<string, string> = {};
        (Array.isArray(r.data) ? (r.data as any[]) : []).forEach((g) => {
          names[String(g._id ?? g.id)] = g.name;
        });
        setGroupNames(names);
      })
      .catch(() => setGroupNames({}));
    void next();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ops]);

  const handleConfirm = async (selection: ScheduleSelection | null) => {
    if (!prompt) return;
    setConfirming(true);
    try {
      await commit(prompt, selection);
      await next();
    } catch (err: any) {
      addToast(
        translateError(err, t, prompt.action === 'add' ? 'bulk.addToGroupError' : 'bulk.removeFromGroupError'),
        'error',
      );
    } finally {
      setConfirming(false);
    }
  };

  const handleClose = () => {
    tallyRef.current.skipped += 1;
    void next();
  };

  const groupName = prompt ? groupNames[prompt.groupId] : undefined;
  const baseTitle = t(prompt?.action === 'remove' ? 'schedulePrompt.titleRemove' : 'schedulePrompt.titleAdd');

  return (
    <SchedulePromptModal
      isOpen={!!prompt}
      onClose={handleClose}
      action={prompt?.action ?? 'add'}
      title={groupName ? `${baseTitle} — ${groupName}` : baseTitle}
      memberLabel={prompt ? t('schedulePrompt.membersLabel', {
        names: prompt.memberIds.map((id) => memberNames[String(id)] ?? `#${id}`).join(', '),
      }) : undefined}
      preview={prompt?.preview ?? null}
      previewLoading={!!prompt && !prompt.preview && !prompt.error}
      previewError={prompt?.error ?? null}
      selectable={prompt?.preview?.selectable ?? false}
      onConfirm={handleConfirm}
      confirming={confirming}
      memberNames={memberNames}
      paymentsLink="/admin/payments/overview"
    />
  );
};

export default GroupChangePrompts;
